import { useState, useEffect, useMemo } from 'react'
import type { Session } from '@supabase/supabase-js'

import { supabase, getHouseholdName } from './lib/supabase'
import { useTransactions }  from './hooks/useTransactions'
import { useCards }         from './hooks/useCards'
import { useCategories }    from './hooks/useCategories'
import { useAnalytics }     from './hooks/useAnalytics'
import { useSubscription }  from './hooks/useSubscription'
import { useNotifications } from './hooks/useNotifications'
import { useWhatsApp }      from './hooks/useWhatsApp'
import { useHousehold }     from './hooks/useHousehold'
import { useCookieConsent } from './hooks/useCookieConsent'
import { useTours }         from './hooks/useTours'
import { useInviteFlow }    from './hooks/useInviteFlow'
import { useCoupleInvite } from './hooks/useCoupleInvite'
import { useAppUI }         from './hooks/useAppUI'
import { useGoals }         from './hooks/useGoals'

import Sidebar             from './components/Sidebar'
import Topbar              from './components/Topbar'
import SummaryCards        from './components/SummaryCards'
import MobileHero          from './components/MobileHero'
import BottomNav           from './components/BottomNav'
import TransactionList     from './components/TransactionList'
import RecentTransactionsList from './components/RecentTransactionsList'
import CategoryBreakdown   from './components/CategoryBreakdown'
import CardsPanel          from './components/CardsPanel'
import AnnualChart         from './components/AnnualChart'
import AnnualView          from './components/AnnualView'
import AddTransactionSheet from './components/AddTransactionSheet'
import FaturaSheet         from './components/FaturaSheet'
import AuthScreen          from './components/AuthScreen'
import CategoryManager     from './components/CategoryManager'
import PricingPlans        from './components/PricingPlans'
import LandingPage         from './components/LandingPage'
import PrivacyPolicy       from './components/PrivacyPolicy'
import TermsOfService      from './components/TermsOfService'
import AccountSettings     from './components/AccountSettings'
import InviteAcceptScreen  from './components/InviteAcceptScreen'
import CookieBanner        from './components/CookieBanner'
import AdminPanel          from './components/AdminPanel'
import { ErrorBoundary }   from './components/ErrorBoundary'
import GoalsPanel          from './components/GoalsPanel'
import CoupleInviteBanner  from './components/CoupleInviteBanner'
import AmortizeModal       from './components/AmortizeModal'
import DeleteGroupModal    from './components/DeleteGroupModal'
import OnboardingTour      from './components/OnboardingTour'
import AddTransactionTour  from './components/AddTransactionTour'
import SetupChecklist      from './components/SetupChecklist'
import { getToastErrorMessage, useToast } from './components/Toast'

import type { TransactionFormData, Transaction, Card } from './types'

const ADMIN_EMAIL = 'hellobagcash@gmail.com'
const currentYear = new Date().getFullYear()

export default function App() {
  const toast = useToast()

  // ── Auth state ───────────────────────────────────────────────────────────────
  const [session,     setSession]     = useState<Session | null>(null)
  const [loadingAuth, setLoadingAuth] = useState(true)
  const [pathname,    setPathname]    = useState(() => window.location.pathname)

  // ── Custom hooks ─────────────────────────────────────────────────────────────
  const ui      = useAppUI()
  const household = useHousehold(session?.user.id)
  const coupleInvite = useCoupleInvite(session?.user.id, household.householdId)
  const { cookieConsent, handleCookieAccept, handleCookieReject } = useCookieConsent(session, pathname)
  const invite  = useInviteFlow()
  const tours   = useTours({
    session,
    loadingAuth,
    householdLoading: household.householdLoading,
    householdId: household.householdId,
    sheetOpen: ui.sheetOpen,
    editingTx: ui.editingTx,
  })

  // ── Auth effects ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(console.error)
    }
  }, [])

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s)
      if (event === 'INITIAL_SESSION') setLoadingAuth(false)
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        if (s?.user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
          ui.setActive('admin')
        } else if (event === 'SIGNED_IN') {
          if (window.location.pathname === '/' || window.location.pathname === '/login') {
            ui.navigate('overview')
            setPathname('/dashboard')
          }
        }
      }
      if (event === 'SIGNED_OUT') {
        window.history.pushState({}, '', '/')
        setPathname('/')
      }
    })
    return () => subscription.unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = () => setPathname(window.location.pathname)
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [])

  // Redirect authenticated user from public/unknown paths to /dashboard
  useEffect(() => {
    if (!session || loadingAuth) return
    const path = window.location.pathname
    const appPaths = ['/dashboard', '/transactions', '/cards', '/annual', '/goals', '/settings', '/admin']
    if (!appPaths.includes(path)) {
      window.history.replaceState({}, '', '/dashboard')
      setPathname('/dashboard')
    }
  }, [session?.user.id, loadingAuth]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data hooks ───────────────────────────────────────────────────────────────
  const { transactions, loading: txLoading, error: txError, addTransaction, editTransaction, editInstallmentGroup, removeTransaction, removeInstallmentGroup, amortizeInstallments, toggleRealized, markInvoiceAsPaid } = useTransactions(household.householdId, ui.currentMonthIndex, currentYear)
  const [amortizingTx,    setAmortizingTx]    = useState<Transaction | null>(null)
  const [deletingGroupTx, setDeletingGroupTx] = useState<Transaction | null>(null)
  const { cards, error: cardsError, addCard, editCard, removeCard }             = useCards(household.householdId)
  const { categories, subcategories, error: categoriesError, addCategory, addSubcategory, editCategory, setLimit, removeCategory } = useCategories(household.householdId)
  const { summary, categories: breakdown, annual, error: analyticsError, refetch: refetchAnalytics } = useAnalytics(household.householdId, ui.currentMonthIndex, currentYear)
  const { subscription: userSubscription, planTier, loading: subLoading, error: subscriptionError, upgrade, cancel: cancelSub, refresh: refreshSubscription } = useSubscription(session?.user.id, household.householdId)
  const { goals } = useGoals(household.householdId)
  const notifications = useNotifications(session?.user.id)
  const whatsApp = useWhatsApp(planTier)

  useEffect(() => {
    if (txError) toast.error(getToastErrorMessage(txError, 'Erro ao carregar lançamentos.'))
  }, [txError, toast])

  useEffect(() => {
    if (cardsError) toast.error(getToastErrorMessage(cardsError, 'Erro ao carregar cartões.'))
  }, [cardsError, toast])

  useEffect(() => {
    if (categoriesError) toast.error(getToastErrorMessage(categoriesError, 'Erro ao carregar categorias.'))
  }, [categoriesError, toast])

  useEffect(() => {
    if (subscriptionError) toast.error(getToastErrorMessage(subscriptionError, 'Erro ao carregar assinatura.'))
  }, [subscriptionError, toast])

  useEffect(() => {
    if (analyticsError) toast.error(getToastErrorMessage(analyticsError, 'Erro ao carregar resumo financeiro.'))
  }, [analyticsError, toast])

  // Bridge: refresh subscription when household members change
  useEffect(() => {
    if (household.membersChangedAt > 0) refreshSubscription()
  }, [household.membersChangedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  const isAdmin = session?.user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()

  const categoryMap = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories])
  const breakdownWithLimits = useMemo(
    () => breakdown.map(cat => ({
      ...cat,
      spending_limit: categoryMap.get(cat.id)?.spending_limit ?? cat.spending_limit ?? null,
    })),
    [breakdown, categoryMap]
  )

  // ── Transaction handlers ─────────────────────────────────────────────────────
  async function handleAdd(data: TransactionFormData): Promise<void> {
    if (!session) return
    try {
      await addTransaction(session.user.id, data)
      refetchAnalytics()
      ui.handleSheetClose()
      toast.success('Lançamento salvo.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao salvar lançamento.'))
      throw err
    }
  }

  async function handleBatchAdd(items: TransactionFormData[]): Promise<void> {
    if (!session) return
    try {
      await Promise.all(items.map(data => addTransaction(session.user.id, data)))
      refetchAnalytics()
      ui.handleSheetClose()
      toast.success(`${items.length} lançamentos importados.`)
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao importar lançamentos.'))
      throw err
    }
  }

  async function handleUpdate(id: string, data: TransactionFormData): Promise<void> {
    try {
      const tx = ui.editingTx
      const wasInstallment = !!tx?.installment_group_id
      const isNowInstallment = data.installment_count > 1

      if (wasInstallment && isNowInstallment && session) {
        await editInstallmentGroup(
          session.user.id,
          tx.installment_group_id!,
          data.date,
          tx.installment_index ?? 1,
          data,
        )
      } else if (!wasInstallment && !isNowInstallment) {
        await editTransaction(id, data)
      } else if (session) {
        if (wasInstallment && tx.installment_group_id) {
          await removeInstallmentGroup(tx.installment_group_id)
        } else {
          await removeTransaction(id)
        }
        await addTransaction(session.user.id, data)
      }
      refetchAnalytics()
      ui.handleSheetClose()
      toast.success('Alterações salvas.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao salvar alterações.'))
      throw err
    }
  }

  async function handleRemove(id: string): Promise<void> {
    try {
      await removeTransaction(id)
      refetchAnalytics()
      toast.success('Lançamento excluído.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao excluir lançamento.'))
      throw err
    }
  }

  async function handleDeleteOne(): Promise<void> {
    if (!deletingGroupTx) return
    try {
      await removeTransaction(deletingGroupTx.id)
      refetchAnalytics()
      setDeletingGroupTx(null)
      toast.success('Parcela excluída.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao excluir parcela.'))
    }
  }

  async function handleDeleteAll(): Promise<void> {
    if (!deletingGroupTx?.installment_group_id) return
    try {
      await removeInstallmentGroup(deletingGroupTx.installment_group_id)
      refetchAnalytics()
      setDeletingGroupTx(null)
      toast.success('Parcelamento excluído.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao excluir parcelamento.'))
    }
  }

  async function handleAmortizeConfirm(count: number): Promise<void> {
    if (!amortizingTx?.installment_group_id || amortizingTx.installment_index == null) return
    const m = ui.currentMonthIndex
    const y = currentYear
    const targetDate = `${y}-${String(m).padStart(2, '0')}-01`
    try {
      await amortizeInstallments(amortizingTx.installment_group_id, amortizingTx.installment_index, targetDate, count)
      refetchAnalytics()
      setAmortizingTx(null)
      toast.success('Parcelas antecipadas.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao antecipar parcelas.'))
    }
  }

  async function handleToggleRealized(id: string, realized: boolean): Promise<void> {
    try {
      await toggleRealized(id, realized)
      refetchAnalytics()
      toast.success(realized ? 'Lançamento marcado como pago.' : 'Lançamento marcado como pendente.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao atualizar lançamento.'))
    }
  }

  async function handleMarkInvoiceAsPaid(cardId: string): Promise<void> {
    try {
      await markInvoiceAsPaid(cardId)
      refetchAnalytics()
      toast.success('Fatura marcada como paga.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao marcar fatura como paga.'))
    }
  }

  async function handleAddCard(card: Omit<Card, 'id'>): Promise<Card> {
    try {
      const created = await addCard(card)
      toast.success('Cartão criado.')
      return created
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao criar cartão.'))
      throw err
    }
  }

  async function handleEditCard(id: string, changes: Partial<Omit<Card, 'id'>>): Promise<void> {
    try {
      await editCard(id, changes)
      toast.success('Cartão atualizado.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao atualizar cartão.'))
      throw err
    }
  }

  async function handleRemoveCard(id: string): Promise<void> {
    try {
      await removeCard(id)
      toast.success('Cartão excluído.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao excluir cartão.'))
      throw err
    }
  }

  async function handleAddCategory(name: string, kind: 'in' | 'out', spendingLimit?: number | null) {
    try {
      const created = await addCategory(name, kind, spendingLimit)
      toast.success('Categoria criada.')
      return created
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao criar categoria.'))
      throw err
    }
  }

  async function handleAddSubcategory(name: string, parentId: string): Promise<void> {
    try {
      await addSubcategory(name, parentId)
      toast.success('Subcategoria criada.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao criar subcategoria.'))
      throw err
    }
  }

  async function handleEditCategory(id: string, name: string): Promise<void> {
    try {
      await editCategory(id, name)
      toast.success('Categoria atualizada.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao atualizar categoria.'))
      throw err
    }
  }

  async function handleSetLimit(id: string, limit: number | null): Promise<void> {
    try {
      await setLimit(id, limit)
      toast.success('Limite atualizado.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao atualizar limite.'))
      throw err
    }
  }

  async function handleRemoveCategory(id: string): Promise<{ error?: string }> {
    const result = await removeCategory(id)
    if (result.error) {
      toast.error('Categoria em uso. Remova as transações antes.')
    } else {
      toast.success('Categoria excluída.')
    }
    return result
  }

  async function handleUpgrade(plan: 'pro' | 'casal', cycle: 'monthly' | 'annual'): Promise<void> {
    try {
      toast.info('Abrindo checkout...')
      await upgrade(plan, cycle, household.householdId, session?.user.email ?? '')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao iniciar assinatura.'))
      throw err
    }
  }

  async function handleCancelSubscription(): Promise<void> {
    try {
      await cancelSub()
      toast.success('Assinatura cancelada.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao cancelar assinatura.'))
    }
  }

  async function handleSignOut(): Promise<void> {
    try {
      await supabase.auth.signOut()
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao sair da conta.'))
    }
  }

  const cookieBanner = cookieConsent === null
    ? <CookieBanner onAccept={handleCookieAccept} onReject={handleCookieReject} />
    : null

  // ── Guards ───────────────────────────────────────────────────────────────────
  if (pathname === '/privacidade' || pathname === '/privacy') {
    window.history.replaceState({}, '', '/privacy')
    return <PrivacyPolicy onBack={() => { window.history.back(); setPathname(window.location.pathname) }} />
  }
  if (pathname === '/termos' || pathname === '/terms') {
    window.history.replaceState({}, '', '/terms')
    return <TermsOfService onBack={() => { window.history.back(); setPathname(window.location.pathname) }} />
  }

  if (loadingAuth) return <div className="bc-loading">Carregando…</div>

  const goToLogin   = () => { window.history.pushState({}, '', '/login'); setPathname('/login') }
  const goToLanding = () => { window.history.pushState({}, '', '/');     setPathname('/') }

  if (invite.inviteToken) {
    if (!session && invite.pendingAutoAccept) return (
      <>
        <AuthScreen onBack={() => invite.setPendingAutoAccept(false)} />
        {cookieBanner}
      </>
    )

    if (session && household.householdLoading) return <div className="bc-loading">Carregando…</div>

    return (
      <>
        <InviteAcceptScreen
          token={invite.inviteToken}
          isAuthenticated={!!session}
          autoAccept={invite.pendingAutoAccept && !!session}
          onNeedsAuth={() => invite.setPendingAutoAccept(true)}
          onAccepted={async (newHouseholdId) => {
            invite.clearInviteToken()
            household.setHouseholdId(newHouseholdId)
            const name = await getHouseholdName(newHouseholdId)
            household.setHouseholdName(name)
            if (session?.user.id) await household.refreshMembers(newHouseholdId, session.user.id)
          }}
          onDeclined={invite.clearInviteToken}
        />
        {cookieBanner}
      </>
    )
  }

  if (household.householdLoading) return <div className="bc-loading">Carregando…</div>

  if (!session && pathname !== '/login') return (
    <>
      <LandingPage onSignIn={goToLogin} />
      {cookieBanner}
    </>
  )
  if (!session) return (
    <>
      <AuthScreen onBack={goToLanding} />
      {cookieBanner}
    </>
  )
  if (household.householdError) return (
    <div className="bc-loading" style={{ flexDirection: 'column', gap: '1rem' }}>
      <p style={{ color: 'var(--expense-500, #e25c4a)', margin: 0, textAlign: 'center' }}>
        Não foi possível carregar seus dados. Tente novamente.
      </p>
      <button className="bc-btn bc-btn--primary" onClick={handleSignOut}>
        Sair
      </button>
    </div>
  )
  if (!household.householdId) return <div className="bc-loading">Carregando…</div>

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="bc-app">
      <Sidebar
        active={ui.active}
        onNav={ui.navigate}
        onSignOut={handleSignOut}
        userName={session.user.user_metadata?.full_name ?? session.user.user_metadata?.name ?? session.user.email?.split('@')[0] ?? ''}
        householdName={household.householdName}
        planTier={planTier}
        memberCount={household.memberCount}
        partnerName={household.partnerName}
        isAdmin={isAdmin}
      />

      <div>
        <Topbar
          active={ui.active}
          month={ui.month}
          year={currentYear}
          annualYear={ui.annualYear}
          isCurrentMonth={ui.isCurrentMonth}
          onAdd={() => ui.setSheetOpen(true)}
          onPrevMonth={ui.prevMonth}
          onNextMonth={ui.nextMonth}
          onGoToCurrentMonth={ui.goToCurrentMonth}
          onPrevYear={() => ui.setAnnualYear(y => y - 1)}
          onNextYear={() => ui.setAnnualYear(y => y + 1)}
        />

        <main className="bc-main">
          {planTier === 'casal' && !household.partnerName && ui.active !== 'settings' && !subLoading && (
            <CoupleInviteBanner
              pendingInvite={coupleInvite.pendingInvite}
              onGoToSettings={() => ui.navigate('settings')}
              onCancelInvite={coupleInvite.cancelInvite}
            />
          )}

          {(ui.active === 'overview' || ui.active === 'transactions') && (
            <>
              <MobileHero
                realizedIncome={summary?.realizedIncome ?? 0}
                projectedIncome={summary?.projectedIncome ?? 0}
                realizedExpense={summary?.realizedExpense ?? 0}
                projectedExpense={summary?.projectedExpense ?? 0}
                realizedBalance={summary?.realizedBalance ?? 0}
                projectedBalance={summary?.projectedBalance ?? 0}
                realizedPct={summary?.realizedPct ?? 0}
                projectedPct={summary?.projectedPct ?? 0}
              />
              <section className="bc-section bc-desktop-only">
                <SummaryCards
                  realizedIncome={summary?.realizedIncome ?? 0}
                  projectedIncome={summary?.projectedIncome ?? 0}
                  realizedExpense={summary?.realizedExpense ?? 0}
                  projectedExpense={summary?.projectedExpense ?? 0}
                  realizedBalance={summary?.realizedBalance ?? 0}
                  projectedBalance={summary?.projectedBalance ?? 0}
                  realizedPct={summary?.realizedPct ?? 0}
                  projectedPct={summary?.projectedPct ?? 0}
                />
              </section>
            </>
          )}

          {ui.active === 'overview' && (
            <>
              <section className="bc-section" style={{ paddingBottom: 0 }}>
                <SetupChecklist 
                  transactions={transactions} 
                  cards={cards} 
                  goals={goals}
                  onOpenAddTransaction={() => ui.setSheetOpen(true)}
                  onNavigateToCards={() => ui.navigate('cards')}
                  onNavigateToGoals={() => ui.navigate('goals')}
                />
              </section>
              <section className="bc-section bc-grid-2">
                <RecentTransactionsList
                  items={transactions}
                  cards={cards}
                  subcategories={subcategories}
                  onAdd={() => ui.setSheetOpen(true)}
                  loading={txLoading}
                  onDelete={handleRemove}
                  onEdit={ui.handleEditClick}
                  onAmortize={setAmortizingTx}
                  onDeleteGroup={setDeletingGroupTx}
                  onToggleRealized={handleToggleRealized}
                  onViewInvoice={ui.setFaturaCard}
                />
                <CategoryBreakdown items={breakdownWithLimits} planTier={planTier} />
              </section>
              <section className="bc-section bc-grid-2">
                <AnnualChart months={annual} />
                <CardsPanel cards={cards} transactions={transactions} onAdd={handleAddCard} onEdit={handleEditCard} onDelete={handleRemoveCard} onViewInvoice={ui.setFaturaCard} planTier={planTier} onUpgrade={() => ui.navigate('settings')} />
              </section>
            </>
          )}

          {ui.active === 'transactions' && (
            <section className="bc-section bc-grid-2">
              <TransactionList
                title="Transações"
                items={transactions}
                cards={cards}
                subcategories={subcategories}
                onAdd={() => ui.setSheetOpen(true)}
                loading={txLoading}
                onDelete={handleRemove}
                onEdit={ui.handleEditClick}
                onAmortize={setAmortizingTx}
                onDeleteGroup={setDeletingGroupTx}
                onToggleRealized={handleToggleRealized}
                onViewInvoice={ui.setFaturaCard}
              />
              <CategoryBreakdown items={breakdownWithLimits} planTier={planTier} />
            </section>
          )}

          {ui.active === 'cards' && (
            <section className="bc-section">
              <CardsPanel cards={cards} transactions={transactions} onAdd={handleAddCard} onEdit={handleEditCard} onDelete={handleRemoveCard} onViewInvoice={ui.setFaturaCard} planTier={planTier} onUpgrade={() => ui.navigate('settings')} />
            </section>
          )}

          {ui.active === 'annual' && (
            <AnnualView
              householdId={household.householdId}
              year={ui.annualYear}
              onAdd={() => ui.setSheetOpen(true)}
            />
          )}

          {ui.active === 'goals' && (
            <section className="bc-section">
              <GoalsPanel householdId={household.householdId} />
            </section>
          )}

          {isAdmin && ui.active === 'admin' && <AdminPanel adminEmail={ADMIN_EMAIL} />}

          {ui.active === 'settings' && (
            <ErrorBoundary>
            <section className="bc-section">
              {planTier === 'free' && (
                <PricingPlans
                  planTier={planTier}
                  subscription={userSubscription}
                  loading={subLoading}
                  onUpgrade={handleUpgrade}
                  onCancel={handleCancelSubscription}
                />
              )}
              <CategoryManager
                categories={categories}
                subcategories={subcategories}
                onAdd={handleAddCategory}
                onAddSubcategory={handleAddSubcategory}
                onRename={handleEditCategory}
                onSetLimit={handleSetLimit}
                onDelete={handleRemoveCategory}
              />
              <AccountSettings
                userId={session.user.id}
                userEmail={session.user.email ?? ''}
                householdId={household.householdId}
                planTier={planTier}
                subscription={userSubscription}
                transactions={transactions}
                cards={cards}
                categories={categories}
                coupleInvite={coupleInvite}
                onAllTransactionsCleared={refetchAnalytics}
                notifications={notifications}
                whatsApp={{ phone: whatsApp.phone, loading: whatsApp.loading, error: whatsApp.error }}
                onRegisterPhone={whatsApp.register}
                onUnregisterPhone={whatsApp.unregister}
                onStartTour={() => {
                  ui.navigate('overview')
                  localStorage.removeItem('bc_tour_done')
                  setTimeout(() => tours.setShowTour(true), 150)
                }}
                onUnlinked={async (newHouseholdId) => {
                  household.setHouseholdId(newHouseholdId)
                  const name = await getHouseholdName(newHouseholdId)
                  household.setHouseholdName(name)
                  if (session?.user.id) await household.refreshMembers(newHouseholdId, session.user.id)
                }}
              />
            </section>
            </ErrorBoundary>
          )}
        </main>
      </div>

      <BottomNav active={ui.active} onNav={ui.navigate} onAdd={() => ui.setSheetOpen(true)} onSignOut={handleSignOut} isAdmin={isAdmin} />

      <AddTransactionSheet
        open={ui.sheetOpen}
        onClose={ui.handleSheetClose}
        onSubmit={handleAdd}
        onBatchSubmit={handleBatchAdd}
        onUpdate={handleUpdate}
        categories={categories}
        subcategories={subcategories}
        cards={cards}
        addCard={handleAddCard}
        addCategory={handleAddCategory}
        editing={ui.editingTx ?? undefined}
        planTier={planTier}
      />

      <FaturaSheet
        open={ui.faturaCard !== null}
        onClose={() => ui.setFaturaCard(null)}
        card={ui.faturaCard}
        transactions={transactions}
        onMarkAsPaid={handleMarkInvoiceAsPaid}
        onEdit={ui.handleEditClick}
        onDelete={handleRemove}
        onDeleteGroup={setDeletingGroupTx}
        onAmortize={setAmortizingTx}
      />

      <AmortizeModal
        key={amortizingTx?.id ?? 'amortize-closed'}
        transaction={amortizingTx}
        onConfirm={handleAmortizeConfirm}
        onClose={() => setAmortizingTx(null)}
      />

      <DeleteGroupModal
        key={deletingGroupTx?.id ?? 'delete-group-closed'}
        transaction={deletingGroupTx}
        onDeleteOne={handleDeleteOne}
        onDeleteAll={handleDeleteAll}
        onClose={() => setDeletingGroupTx(null)}
      />

      {tours.showTour && <OnboardingTour onDone={tours.handleTourDone} />}
      <AddTransactionTour active={tours.showSheetTour} onDone={tours.handleSheetTourDone} />

      {cookieBanner}
    </div>
  )
}
